import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Search, Pencil, Trash2, MapPin, Phone, Mail, Building2, Tag, X, User, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyAddress = { label: 'Home', street: '', city: '', state: '', pincode: '', country: 'India' };
const emptyContact = { name: '', email: '', phone: '', company: '', addresses: [{ ...emptyAddress }], notes: '', tags: [] };

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyContact });
  const [tagInput, setTagInput] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchContacts(); fetchTags(); }, [search, selectedTag]);

  const fetchContacts = async () => {
    try {
      let url = `${BACKEND_URL}/api/contacts?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (selectedTag) url += `tag=${encodeURIComponent(selectedTag)}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setContacts(data);
    } catch (e) { console.error(e); }
  };

  const fetchTags = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contacts/tags/all`, { withCredentials: true });
      setAllTags(data);
    } catch (e) {}
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/contacts/${editing.id}`, form, { withCredentials: true });
        toast.success('Contact updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/contacts`, form, { withCredentials: true });
        toast.success('Contact added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyContact, addresses: [{ ...emptyAddress }] });
      fetchContacts();
      fetchTags();
    } catch (e) { toast.error('Failed to save contact'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contacts/${id}`, { withCredentials: true });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const openEdit = (contact) => {
    setEditing(contact);
    setForm({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
      addresses: contact.addresses?.length ? contact.addresses : [{ ...emptyAddress }],
      notes: contact.notes || '',
      tags: contact.tags || []
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyContact, addresses: [{ ...emptyAddress }] });
    setDialogOpen(true);
  };

  const addAddress = () => setForm({ ...form, addresses: [...form.addresses, { ...emptyAddress }] });
  const removeAddress = (i) => setForm({ ...form, addresses: form.addresses.filter((_, idx) => idx !== i) });
  const updateAddress = (i, field, value) => {
    const addrs = [...form.addresses];
    addrs[i] = { ...addrs[i], [field]: value };
    setForm({ ...form, addresses: addrs });
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader title="Hi Tech Industries">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-600" data-testid="back-admin">
          Admin Dashboard
        </Button>
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-1">Contacts</h2>
            <p className="text-sm sm:text-base text-slate-500">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap sm:flex-nowrap">
            <Button onClick={openNew} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="add-contact-button">
              <Plus size={18} className="mr-2" /> Add Contact
            </Button>
            <Button variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/contacts/export/csv`, '_blank')} className="rounded-sm" data-testid="export-csv-button">
              <Download size={16} className="mr-1.5" /> Export
            </Button>
            <label className="inline-flex">
              <input type="file" accept=".csv" className="hidden" data-testid="import-csv-input" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  const { data } = await axios.post(`${BACKEND_URL}/api/contacts/import/csv`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success(data.message);
                  fetchContacts(); fetchTags();
                } catch (err) { toast.error('Import failed'); }
                e.target.value = '';
              }} />
              <Button variant="outline" className="rounded-sm" onClick={(e) => e.currentTarget.parentElement.querySelector('input').click()} data-testid="import-csv-button">
                <Upload size={16} className="mr-1.5" /> Import
              </Button>
            </label>
          </div>
        </div>

        {/* Search & filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone or company..."
              className="pl-9 h-10 rounded-sm border-slate-200"
              data-testid="contact-search-input"
            />
          </div>
          {allTags.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <Button variant={!selectedTag ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTag('')}
                className={`rounded-sm text-xs ${!selectedTag ? 'bg-[#0052FF]' : ''}`} data-testid="filter-all">
                All
              </Button>
              {allTags.map(t => (
                <Button key={t} variant={selectedTag === t ? 'default' : 'outline'} size="sm" onClick={() => setSelectedTag(selectedTag === t ? '' : t)}
                  className={`rounded-sm text-xs ${selectedTag === t ? 'bg-[#0052FF]' : ''}`} data-testid={`filter-tag-${t}`}>
                  {t}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Contact List */}
        {contacts.length === 0 ? (
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardContent className="py-12 text-center">
              <User size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">{search ? 'No contacts match your search' : 'No contacts yet'}</p>
              {!search && <Button onClick={openNew} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm">Add First Contact</Button>}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map(c => (
              <Card key={c.id} className="border border-slate-200 rounded-sm shadow-none hover:-translate-y-0.5 transition-transform duration-150" data-testid={`contact-card-${c.id}`}>
                <CardHeader className="p-4 sm:p-5 pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base sm:text-lg font-medium truncate">{c.name}</CardTitle>
                      {c.company && <CardDescription className="text-xs sm:text-sm flex items-center gap-1 mt-0.5"><Building2 size={12} /> {c.company}</CardDescription>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-5 pt-0 space-y-2">
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0052FF]" data-testid={`contact-phone-${c.id}`}>
                      <Phone size={14} className="shrink-0" /> {c.phone}
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0052FF] truncate" data-testid={`contact-email-${c.id}`}>
                      <Mail size={14} className="shrink-0" /> {c.email}
                    </a>
                  )}

                  {/* Addresses */}
                  {c.addresses?.length > 0 && (
                    <div className="pt-2 border-t border-slate-100">
                      {(expandedId === c.id ? c.addresses : c.addresses.slice(0, 1)).map((addr, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-slate-500 mt-1.5" data-testid={`contact-address-${c.id}-${i}`}>
                          <MapPin size={12} className="shrink-0 mt-0.5 text-slate-400" />
                          <span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">{addr.label}:</span>
                            {addr.street}, {addr.city}, {addr.state} {addr.pincode}
                          </span>
                        </div>
                      ))}
                      {c.addresses.length > 1 && (
                        <button onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} className="text-[10px] text-[#0052FF] mt-1 hover:underline" data-testid={`toggle-addresses-${c.id}`}>
                          {expandedId === c.id ? 'Show less' : `+${c.addresses.length - 1} more address${c.addresses.length > 2 ? 'es' : ''}`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2">
                      {c.tags.map(t => (
                        <Badge key={t} className="bg-slate-100 text-slate-600 rounded-sm text-[10px] font-normal">{t}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="contact-name-input" />
              </div>
              <div>
                <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Company</Label>
                <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-sm" data-testid="contact-company-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="contact-email-input" />
              </div>
              <div>
                <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm" data-testid="contact-phone-input" />
              </div>
            </div>

            {/* Addresses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Addresses</Label>
                <Button variant="outline" size="sm" onClick={addAddress} className="rounded-sm h-7 text-xs" data-testid="add-address-button">
                  <Plus size={12} className="mr-1" /> Add Address
                </Button>
              </div>
              {form.addresses.map((addr, i) => (
                <div key={i} className="border border-slate-200 rounded-sm p-3 mb-2 space-y-2 relative" data-testid={`address-form-${i}`}>
                  {form.addresses.length > 1 && (
                    <button onClick={() => removeAddress(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" data-testid={`remove-address-${i}`}>
                      <X size={14} />
                    </button>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Label</Label>
                      <Input value={addr.label} onChange={e => updateAddress(i, 'label', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-label-${i}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-3">
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Street</Label>
                      <Input value={addr.street} onChange={e => updateAddress(i, 'street', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-street-${i}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">City</Label>
                      <Input value={addr.city} onChange={e => updateAddress(i, 'city', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-city-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">State</Label>
                      <Input value={addr.state} onChange={e => updateAddress(i, 'state', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-state-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Pincode</Label>
                      <Input value={addr.pincode} onChange={e => updateAddress(i, 'pincode', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-pincode-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Country</Label>
                      <Input value={addr.country} onChange={e => updateAddress(i, 'country', e.target.value)} className="mt-1 h-8 rounded-sm text-xs" data-testid={`address-country-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div>
              <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Tags</Label>
              <div className="flex gap-2 mt-1.5">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Type and press Enter" className="rounded-sm h-8 text-xs" data-testid="contact-tag-input" />
                <Button variant="outline" size="sm" onClick={addTag} className="rounded-sm h-8 text-xs shrink-0" data-testid="add-tag-button">Add</Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.map(t => (
                    <Badge key={t} className="bg-slate-100 text-slate-700 rounded-sm text-xs pr-1 flex items-center gap-1">
                      {t} <button onClick={() => removeTag(t)} className="hover:text-red-500"><X size={10} /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." className="mt-1.5 rounded-sm" data-testid="contact-notes-input" />
            </div>

            <Button onClick={handleSave} className="w-full bg-[#0052FF] hover:bg-[#0043D1] rounded-sm" data-testid="save-contact-button">
              {editing ? 'Update Contact' : 'Save Contact'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
